/**
 * Org-level severity configuration. ADR-0009.
 *
 * ADR-0009 is explicit that the `0.25` floor, the `1.5` direction multiplier,
 * and the band thresholds are **business thresholds, not constants** — chosen
 * before seeing real data, expected to be recalibrated against a design
 * partner's actual portfolio, and deliberately placed where recalibrating them
 * needs no code change. This is that promise landing: they are stored per
 * organization and edited from the UI.
 *
 * Only the five scalars are persisted, never the assembled
 * `bandThresholdsCents` array. Storing the array raw would let a write put the
 * bands out of order or invent new ones, and "recalibrating the constants"
 * would quietly become "changing the formula's shape" — which ADR-0009 says
 * supersedes it rather than tunes it.
 */
import { z } from 'zod';
import { DEFAULT_SEVERITY_CONFIG } from '@picklii/recon';
const cents = z.number().int().nonnegative();
export const severityConfigSchema = z
    .object({
    /**
     * Weight exposure keeps at zero burn.
     *
     * Must be greater than zero. A floor of 0 is not a tuning choice, it is
     * the alternative ADR-0009 considered and rejected: with no floor a
     * brand-new project scores zero no matter how wrong its budget is, so
     * nobody fixes it while fixing is still free. Accepting 0 here would let
     * a typo reverse a documented decision.
     */
    burnFloor: z.number().gt(0).lte(1),
    /** Multiplier when the PSA budget is too high. Never below parity. */
    directionMultiplier: z.number().gte(1),
    criticalCents: cents,
    highCents: cents,
    mediumCents: cents,
})
    .superRefine((value, ctx) => {
    // Strictly descending. `bandForScore` returns the first threshold a score
    // clears, so an equal or inverted pair makes a band unreachable — a
    // finding could never be Critical, and nothing would say so.
    if (!(value.criticalCents > value.highCents)) {
        ctx.addIssue({
            code: 'custom',
            path: ['criticalCents'],
            message: 'Critical must be above High.',
        });
    }
    if (!(value.highCents > value.mediumCents)) {
        ctx.addIssue({ code: 'custom', path: ['highCents'], message: 'High must be above Medium.' });
    }
    if (!(value.mediumCents > 0)) {
        ctx.addIssue({
            code: 'custom',
            path: ['mediumCents'],
            message: 'Medium must be above zero; Low is everything below it.',
        });
    }
});
export const DEFAULT_STORED_SEVERITY_CONFIG = {
    burnFloor: DEFAULT_SEVERITY_CONFIG.burnFloor,
    directionMultiplier: DEFAULT_SEVERITY_CONFIG.directionMultiplier,
    criticalCents: 1_500_000,
    highCents: 500_000,
    mediumCents: 100_000,
};
/** Rebuild the engine's config from the stored scalars. */
export function toSeverityConfig(stored) {
    return {
        burnFloor: stored.burnFloor,
        directionMultiplier: stored.directionMultiplier,
        bandThresholdsCents: [
            { band: 'Critical', minScore: stored.criticalCents },
            { band: 'High', minScore: stored.highCents },
            { band: 'Medium', minScore: stored.mediumCents },
            { band: 'Low', minScore: 0 },
        ],
    };
}
//# sourceMappingURL=severity-config.js.map